/**
 * The playground runner's clocks, in one place.
 *
 * A headless run has three nested budgets:
 *  1. the SERVE budget — "is the page up": navigation commit + the playground's own READY signal
 *     (a second full budget for the UI shell's iframe, which is a second cold module graph);
 *  2. the RESULT budget — the declared test budgets plus slack;
 *  3. the outer lid the CLI races the whole run against, which must be the SUM of the two above
 *     so it never fires while an inner wait is still legitimately inside its budget.
 *
 * The serve budget is deliberately NOT derived from the test budget: serve latency is a property
 * of the machine (a cold vite serve under load), test length is a property of the game.
 */

import type { PlaygroundNormalizedTestConfig, PlaygroundRuntimeSelection } from './types.js';

/** The variable that sizes the serve budget. */
export const SERVE_TIMEOUT_ENV = 'GOTTI_PLAYGROUND_SERVE_TIMEOUT_MS';
/** The serve budget when the variable is unset. */
export const DEFAULT_SERVE_TIMEOUT_MS = 120_000;

/**
 * Thrown when the serve budget is spent. The message names the clock and what set its budget (`setBy`: the caller's own
 * name for it — the CLI's `--serve-timeout`, the variable, or the default — else the variable that sizes it), then
 * `detail` when there is one — a READY wait's names the page's module requests that were still pending.
 */
export class PlaygroundServeTimeoutError extends Error {
  override readonly name = 'PlaygroundServeTimeoutError';
  readonly clock = 'serve' as const;
  /** What the message says after the budget's own sentence; `undefined` when it says nothing more. */
  readonly detail: string | undefined;
  /** What set the budget, as the message names it; `GOTTI_PLAYGROUND_SERVE_TIMEOUT_MS` when the caller did not say. */
  readonly setBy: string;

  constructor(
    readonly waitingFor: string,
    readonly budgetMs: number,
    readonly elapsedMs: number,
    options?: ErrorOptions & { detail?: string; setBy?: string },
  ) {
    const setBy = options?.setBy ?? SERVE_TIMEOUT_ENV;
    super(
      `Playground serve budget of ${budgetMs}ms (${setBy}) exhausted after ${elapsedMs}ms while waiting for ${waitingFor}.` +
        (options?.detail ? ` ${options.detail}` : ''),
      options,
    );
    this.detail = options?.detail;
    this.setBy = setBy;
  }
}

/** One deadline shared by every wait that makes up "the page is up". */
export interface ServeDeadline {
  readonly budgetMs: number;
  /**
   * Milliseconds left, always an integer >= 1 — Playwright treats a `timeout` of 0 as "no timeout",
   * so a spent budget THROWS the serve-clock error rather than ever handing a 0 to the next wait.
   */
  remaining(waitingFor: string): number;
  /**
   * Run one wait with the time still left. A Playwright `TimeoutError` raised inside it is
   * re-thrown as the serve-clock error (with the original as `cause`); anything else passes through.
   */
  within<T>(waitingFor: string, wait: (timeoutMs: number) => Promise<T>): Promise<T>;
}

/** A Playwright call that gave up on its own `timeout` (Playwright's `TimeoutError`). */
export const isPlaywrightTimeout = (err: unknown): err is Error =>
  err instanceof Error && err.name === 'TimeoutError';

const parsePositiveIntegerMs = (variable: string, raw: string): number => {
  const parsed = /^\d+$/.test(raw) ? Number(raw) : Number.NaN;
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new RangeError(`${variable} must be a positive integer number of milliseconds; received ${JSON.stringify(raw)}.`);
  }
  return parsed;
};

/**
 * One serve deadline for a page: `budgetMs` from `now()`, shared by every wait that makes up "the page is up". Its
 * errors name `setBy` as what set the budget (default: the variable that sizes it).
 */
export const createServeDeadline = (budgetMs: number, now: () => number = Date.now, setBy: string = SERVE_TIMEOUT_ENV): ServeDeadline => {
  const startedAt = now();
  const elapsed = () => now() - startedAt;

  const remaining = (waitingFor: string): number => {
    const left = Math.ceil(budgetMs - elapsed());
    if (left <= 0) {
      throw new PlaygroundServeTimeoutError(waitingFor, budgetMs, Math.round(elapsed()), { setBy });
    }
    return left;
  };

  return {
    budgetMs,
    remaining,
    async within(waitingFor, wait) {
      const timeoutMs = remaining(waitingFor);
      try {
        return await wait(timeoutMs);
      } catch (err) {
        if (isPlaywrightTimeout(err)) {
          throw new PlaygroundServeTimeoutError(waitingFor, budgetMs, Math.round(elapsed()), { cause: err, setBy });
        }
        throw err;
      }
    },
  };
};

/**
 * `GOTTI_PLAYGROUND_SERVE_TIMEOUT_MS`, or the default. Anything that is not a positive integer is a
 * configuration error and is reported as one — a silent fallback would hide a typo until a run
 * died on the wrong clock.
 */
export const resolveServeTimeoutMs = (env: Readonly<Record<string, string | undefined>>): number => {
  const raw = env[SERVE_TIMEOUT_ENV];
  return raw === undefined ? DEFAULT_SERVE_TIMEOUT_MS : parsePositiveIntegerMs(SERVE_TIMEOUT_ENV, raw);
};

/** The CLI's outer lid on a whole headless run. Explicit override only; the default is derived. */
export const RUN_TIMEOUT_ENV = 'GOTTI_PLAYGROUND_TEST_TIMEOUT_MS';

const RESULT_SLACK_MS = 10_000;
const RESULT_FLOOR_MS = 45_000;

/**
 * The longest delay a timer honours: 2^31 − 1 ms (about 24.8 days). Browsers and Node fire a longer
 * `setTimeout` delay at once (Node warns `TimeoutOverflowWarning` and waits 1 ms), so every budget the
 * runner turns into a timer is held to it: a longer step or assertion `budgetMs` is refused at load,
 * and the budgets derived on the node side — the result budget and the CLI's outer lid — are clamped
 * to it, so a huge declared budget waits as long as a timer can instead of failing at once.
 */
export const MAX_TIMER_MS = 2_147_483_647;

/**
 * What a selection's gates add to the result budget: `readyTimeoutMs` once for each gate function the
 * definition declares (`ready`, `game`) — the runtime races each against that budget in turn — and 0
 * when it declares neither (`readyTimeoutMs` is then not read). The one rule both callers of
 * {@link computeResultTimeoutMs} (the runner and the CLI's outer lid) pass as its `gateBudgetMs`.
 */
export const gateBudgetMs = (selection: Pick<PlaygroundRuntimeSelection, 'ready' | 'game' | 'readyTimeoutMs'>): number =>
  (selection.ready ? selection.readyTimeoutMs : 0) + (selection.game ? selection.readyTimeoutMs : 0);

/**
 * The RESULT budget: how long the runner waits for the in-page suite to publish `complete: true`
 * once the page is READY. The SUM of the budgets of `tests` — the tests the run executes, which run one after another,
 * so the suite takes as long as all of them (a scenario's own `timeoutMs` is already the sum of its steps') — plus the
 * gate budget ({@link gateBudgetMs}: `readyTimeoutMs` once per declared `ready`/`game`, 0 with neither) plus slack, with
 * a floor so a suite of tiny tests still gets a sane wait; an explicit override replaces it wholesale. Either is
 * clamped to {@link MAX_TIMER_MS}.
 */
export const computeResultTimeoutMs = (
  tests: ReadonlyArray<Pick<PlaygroundNormalizedTestConfig, 'timeoutMs'>>,
  override?: number,
  /** What the selection's gates add — {@link gateBudgetMs} of the selection; 0 when it declares neither `ready` nor `game`. */
  gateBudgetMs = 0,
): number => {
  if (typeof override === 'number') {
    return Math.min(override, MAX_TIMER_MS);
  }
  const sum = tests.reduce((total, test) => total + test.timeoutMs, 0);
  return Math.min(Math.max(sum + gateBudgetMs + RESULT_SLACK_MS, RESULT_FLOOR_MS), MAX_TIMER_MS);
};

/**
 * Thrown when the suite did not publish its result inside the RESULT budget. Distinct from
 * {@link PlaygroundServeTimeoutError} so a reader can tell a slow boot from a slow test — which is
 * the entire reason the two budgets are separate.
 */
export class PlaygroundResultTimeoutError extends Error {
  override readonly name = 'PlaygroundResultTimeoutError';
  readonly clock = 'result' as const;

  constructor(readonly budgetMs: number, options?: ErrorOptions) {
    super(
      `The playground suite did not finish within the result budget of ${budgetMs}ms ` +
      `(the declared timeoutMs of every test the run executes, summed — they run one after another — plus readyTimeoutMs per ` +
      `declared ready/game gate, plus slack). Raise the test's own timeoutMs, or pass ` +
      `timeoutMs to runPlaygroundTests. This is NOT the serve budget (${SERVE_TIMEOUT_ENV}) — the page was already up.`,
      options,
    );
  }
}

/** What the CLI's outer lid ({@link resolveRunLidMs}) is derived from: the budgets it encloses, and the environment whose variable replaces it. */
export interface RunLidInputs {
  /** One page's serve budget: the CLI's `--serve-timeout`, else {@link resolveServeTimeoutMs}. */
  serveTimeoutMs: number;
  /** The result budget: {@link computeResultTimeoutMs} of the tests the run executes. */
  resultTimeoutMs: number;
  /** 1 on the direct path; 2 through the UI shell (the shell, then the iframe's own cold graph). */
  serveBudgets: 1 | 2;
  /**
   * The waits the runner's `onReady` budgets, between READY and the result budget (which starts only once `onReady` has
   * finished); default 0. The CLI's `--video` render anchor is one: its timeout plus one poll interval, the most its
   * polling can take — each of its screenshots is given only what is left of that wait. The single page calls around
   * it (the GPU check, the preview iframe's rect, the canvas survey of each poll) carry no timeout and take no budget of
   * their own: on a page that has stopped answering they end when the lid does.
   */
  onReadyBudgetMs?: number;
  /** Where `GOTTI_PLAYGROUND_TEST_TIMEOUT_MS` ({@link RUN_TIMEOUT_ENV}) is read: set, it replaces the derived lid outright. */
  env: Readonly<Record<string, string | undefined>>;
}

/**
 * The outer lid the CLI races a run against. Derived as the SUM of the budgets it encloses so it
 * can never fire while an inner wait is still legitimately inside its own budget; an explicit
 * `GOTTI_PLAYGROUND_TEST_TIMEOUT_MS` wins outright for CI environments that want a hard cap. Either
 * is clamped to {@link MAX_TIMER_MS}: the lid is a `setTimeout`.
 */
export const resolveRunLidMs = ({ serveTimeoutMs, resultTimeoutMs, serveBudgets, onReadyBudgetMs = 0, env }: RunLidInputs): number => {
  const explicit = env[RUN_TIMEOUT_ENV];
  if (explicit !== undefined) {
    return Math.min(parsePositiveIntegerMs(RUN_TIMEOUT_ENV, explicit), MAX_TIMER_MS);
  }
  return Math.min(serveBudgets * serveTimeoutMs + onReadyBudgetMs + resultTimeoutMs, MAX_TIMER_MS);
};

/** The tests a run with `targetLabel` will actually execute — a target matches by label or by id. */
export const testsMatchingLabel = <T extends Pick<PlaygroundNormalizedTestConfig, 'id' | 'label'>>(
  tests: readonly T[],
  targetLabel: string | undefined,
): T[] => (targetLabel === undefined ? [...tests] : tests.filter((test) => test.label === targetLabel || test.id === targetLabel));
